import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Users, ArrowRight, Loader2, Check } from 'lucide-react';
import { billingService } from '../../services/billingService';
import { workspaceService } from '../../services/workspaceService';
import { projectService } from '../../services/projectService';
import { SeatCheckoutModal } from './SeatCheckoutModal';
// AtlasSeatReview is the "Your team on Atlas" surface (PAYG->Atlas re-basing
// seam). Atlas changes what's included — 2 free workspace editor seats, ZERO
// free project collaborators — so this panel offers the fair moment-of-change
// choice per person: move them into an included workspace seat (free, but they
// can then edit EVERY workspace project) or keep them project-scoped at
// $14/mo. Confirming kept seats applies the re-based math then and there via
// ConfirmProjectSeatRebase; nothing is ever re-priced silently.
export const AtlasSeatReview = ({ onChanged }) => {
    const [state, setState] = useState(null);
    const [accountId, setAccountId] = useState(null);
    const [busyUserId, setBusyUserId] = useState(null);
    const [movedUserIds, setMovedUserIds] = useState(new Set());
    const [rowError, setRowError] = useState(null);
    const [confirming, setConfirming] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [confirmError, setConfirmError] = useState(null);
    const [seatCheckoutSecret, setSeatCheckoutSecret] = useState(null);
    const [seatCheckoutSessionId, setSeatCheckoutSessionId] = useState(null);
    const load = useCallback(async () => {
        try {
            const [review, workspaces] = await Promise.all([
                billingService.getSeatReviewState(),
                workspaceService.listMyWorkspaces(),
            ]);
            setState(review);
            const active = workspaces.find(w => w.isActive) || workspaces[0];
            setAccountId(active ? active.id : null);
        }
        catch (_a) {
            // Review is a courtesy surface: failing to load renders nothing rather
            // than blocking the Atlas success screen.
            setState(null);
        }
    }, []);
    useEffect(() => {
        load();
    }, [load]);
    // One row per person; a person collaborating on several projects moves (or
    // stays) as a whole, so their project grants are grouped.
    const people = useMemo(() => {
        if (!state)
            return [];
        const byUser = new Map();
        for (const c of state.collaborators) {
            const existing = byUser.get(c.userId);
            const displayName = `${c.firstName} ${c.lastName}`.trim() || c.email;
            if (existing) {
                existing.projects.push({ id: c.projectId, name: c.projectName });
            }
            else {
                byUser.set(c.userId, {
                    userId: c.userId,
                    email: c.email,
                    name: displayName,
                    projects: [{ id: c.projectId, name: c.projectName }],
                });
            }
        }
        return Array.from(byUser.values());
    }, [state]);
    if (!state || !state.hasUnlimited || people.length === 0)
        return null;
    const keptCount = people.filter(p => !movedUserIds.has(p.userId)).length;
    const projectSeatPrice = state.projectSeatPriceCents / 100;
    const keptMonthly = keptCount * projectSeatPrice;
    const editorHeadroom = Math.max(0, state.includedWorkspaceEditorSeats - state.activeWorkspaceMembers);
    // Move = invite into the workspace (existing rail, editor-seat metering and
    // all), then remove each project-collaborator grant. Invite-first so their
    // access never gaps; an invite failure leaves the grants untouched.
    const movePerson = async (person) => {
        if (!accountId)
            return;
        setBusyUserId(person.userId);
        setRowError(null);
        try {
            await workspaceService.inviteWorkspaceMember({
                accountId,
                email: person.email,
                role: 'member',
            });
            for (const p of person.projects) {
                await projectService.removeCollaborator(p.id, person.userId);
            }
            setMovedUserIds(prev => new Set(prev).add(person.userId));
            setConfirmed(false);
            onChanged === null || onChanged === void 0 ? void 0 : onChanged();
        }
        catch (e) {
            const msg = (e === null || e === void 0 ? void 0 : e.message) || '';
            setRowError({
                userId: person.userId,
                message: msg.includes('ALREADY_EXISTS') || msg.toLowerCase().includes('already')
                    ? 'They already hold a workspace seat — you can keep or remove their project grants from the project settings.'
                    : msg || 'Could not move them. Please try again.',
            });
        }
        finally {
            setBusyUserId(null);
        }
    };
    // Keep = apply the re-based project-seat math with consent. needsCheckout
    // means there's no seat subscription yet: run the existing first-seat rail.
    const confirmKept = async () => {
        setConfirming(true);
        setConfirmError(null);
        try {
            const result = await billingService.confirmProjectSeatRebase();
            if (result.needsCheckout) {
                const checkout = await billingService.initiateCheckout('project_seat');
                setSeatCheckoutSecret(checkout.clientSecret);
                setSeatCheckoutSessionId(checkout.checkoutSessionId);
            }
            else {
                setConfirmed(true);
                onChanged === null || onChanged === void 0 ? void 0 : onChanged();
                await load();
            }
        }
        catch (e) {
            setConfirmError((e === null || e === void 0 ? void 0 : e.message) || 'Could not confirm seats. Please try again.');
        }
        finally {
            setConfirming(false);
        }
    };
    return (_jsxs("div", { className: "mt-5 rounded-lg border-2 p-4 text-left", style: { backgroundColor: 'var(--dew)', borderColor: 'var(--forest)' }, children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Users, { size: 16, style: { color: 'var(--forest)' } }), _jsx("p", { className: "text-sm font-semibold", style: { color: 'var(--forest)' }, children: "Your team on Atlas" })] }), _jsxs("p", { className: "mt-1.5 text-xs leading-relaxed", style: { color: 'var(--charcoal)', opacity: 0.85 }, children: ["Atlas includes ", state.includedWorkspaceEditorSeats - 1, " workspace editor seats (you plus", ' ', state.includedWorkspaceEditorSeats - 1 === 1 ? 'one' : 'two', ") \u2014 workspace editors can edit", ' ', _jsx("span", { className: "font-semibold", children: "every project in your workspace" }), ". Project-level collaborators are $", projectSeatPrice, "/month each on Atlas. Choose for each person:"] }), _jsx("div", { className: "mt-3 flex flex-col gap-2", children: people.map(person => {
                    const moved = movedUserIds.has(person.userId);
                    const busy = busyUserId === person.userId;
                    return (_jsxs("div", { className: "rounded-md border px-3 py-2 flex flex-col gap-1", style: { backgroundColor: 'var(--sand)', borderColor: 'var(--pewter)' }, children: [_jsxs("div", { className: "flex items-center justify-between gap-3", children: [_jsxs("div", { className: "min-w-0", children: [_jsx("p", { className: "text-sm font-semibold truncate", style: { color: 'var(--charcoal)' }, children: person.name }), _jsx("p", { className: "text-[11px] truncate", style: { color: 'var(--charcoal)', opacity: 0.7 }, children: person.projects.map(p => p.name).join(', ') })] }), moved ? (_jsxs("span", { className: "flex items-center gap-1 text-xs font-semibold flex-shrink-0", style: { color: 'var(--forest)' }, children: [_jsx(Check, { size: 14 }), " Workspace editor"] })) : (_jsxs("button", { onClick: () => movePerson(person), disabled: busy || !accountId, className: "flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border-2 transition-colors duration-200 flex-shrink-0", style: { backgroundColor: 'var(--sand)', color: 'var(--forest)', borderColor: 'var(--forest)' }, title: "They become a workspace editor (included seat) and can edit every project in this workspace", children: [busy ? _jsx(Loader2, { size: 13, className: "animate-spin" }) : _jsx(ArrowRight, { size: 13 }), "Move to workspace seat", editorHeadroom > 0 ? ' — included' : ''] }))] }), (rowError === null || rowError === void 0 ? void 0 : rowError.userId) === person.userId && (_jsx("p", { className: "text-[11px]", style: { color: 'var(--charcoal)', opacity: 0.85 }, children: rowError.message }))] }, person.userId));
                }) }), _jsxs("div", { className: "mt-3 flex items-center justify-between gap-3 flex-wrap", children: [_jsx("p", { className: "text-xs", style: { color: 'var(--charcoal)', opacity: 0.85 }, children: keptCount === 0
                            ? 'Everyone moved — no project seats to bill.'
                            : `Keeping ${keptCount} as project collaborator${keptCount === 1 ? '' : 's'}: $${keptMonthly}/month.` }), keptCount > 0 && !confirmed && (_jsxs("button", { onClick: confirmKept, disabled: confirming, className: "flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-full border-2 transition-colors duration-200", style: { backgroundColor: 'var(--forest)', color: 'var(--sand)', borderColor: 'var(--forest)' }, children: [confirming ? _jsx(Loader2, { size: 13, className: "animate-spin" }) : null, "Keep them & confirm $", keptMonthly, "/month"] })), confirmed && (_jsxs("span", { className: "flex items-center gap-1 text-xs font-semibold", style: { color: 'var(--forest)' }, children: [_jsx(Check, { size: 14 }), " Project seats confirmed"] }))] }), confirmError && (_jsx("p", { className: "mt-1 text-[11px]", style: { color: 'var(--charcoal)', opacity: 0.85 }, children: confirmError })), seatCheckoutSecret && (_jsx(SeatCheckoutModal, { kind: "project", clientSecret: seatCheckoutSecret, checkoutSessionId: seatCheckoutSessionId || '', onClose: () => setSeatCheckoutSecret(null), onComplete: () => {
                    setSeatCheckoutSecret(null);
                    setConfirmed(true);
                    onChanged === null || onChanged === void 0 ? void 0 : onChanged();
                    load();
                }, onError: () => {
                    setSeatCheckoutSecret(null);
                    setConfirmError('The seat checkout could not be completed.');
                } }))] }));
};
